/**
 * Canonical list of subscription `features.*` gate keys editable in admin tooling.
 * Keep in sync with server-side subscription behavior where relevant (e.g. chat/plugins pairs).
 * Register one-click gates with `oneClick` metadata here. Admin Gates uses this
 * capability for list/invite/remove, status columns and the membership summary.
 * Their list route calls loadEnrollmentGateList with this registered option;
 * it reads the entire population and returns both accounts and summary.
 * enrollmentLink is optional outreach guidance. Gate-specific acceptance and
 * welcome behavior remain in the existing enrollment routes.
 */
export type OneClickGateConfig = {
  adminApi: string;
  accessName: string;
  invitationField: string;
  enrollmentField: string;
  enrollmentLink?: string;
};

export type FeatureGateOption = { value: string; label: string; oneClick?: OneClickGateConfig };

export const FEATURE_GATE_OPTIONS = [
  { value: "previewNonAdminFeatures", label: "Preview as non-admin (feature gates only)" },
  { value: "plugins", label: "Plugins" },
  { value: "novelNovember2026", label: "Novel November 2026 preregistrant" },
  { value: "agenticNovelImport", label: "Agentic Novel Import" },
  { value: "adminAgenticChatBrainPicker", label: "Admin Agentic Chat Brain Picker" },
  { value: "chat", label: "Chat" },
  { value: "spicy", label: "Spicy" },
  { value: "sw2", label: "Sudowrite 2.0" },
  { value: "modelsBeta", label: "Models Beta" },
  { value: "quickEdit", label: "Quick Edit" },
  { value: "myVoice", label: "My Voice" },
  { value: "structuredOutline", label: "Structured Outline" },
  { value: "reviser", label: "Reviser" },
  { value: "museBeta", label: "Muse Beta" },
  { value: "muse2Alpha", label: "Muse 2 Alpha Vibe Check" },
  { value: "scenesBeta", label: "Scenes Beta" },
  { value: "cleanCopy", label: "Clean Copy" },
  { value: "chapterGlueBeta", label: "Chapter Glue Beta" },
  { value: "povAndTenseBeta", label: "POV and Tense Beta" },
  { value: "myVoiceV3A", label: "My Voice v3 Group A" },
  { value: "myVoiceV3B", label: "My Voice v3 Group B" },
  { value: "myVoiceWrite", label: "My Voice Write" },
  { value: "aiFunctionEditor", label: "AI Function Editor" },
  { value: "exemptFromAbtesting", label: "Exempt from A/B Testing" },
  { value: "fullSBEditing", label: "Full SB Editing" },
  { value: "styleGuide", label: "Style Guide", oneClick: {
    adminApi: "/api/admin/style-guide-beta", accessName: "Style Guide beta",
    invitationField: "styleGuideBetaInvitation", enrollmentField: "styleGuideBetaEnrollment",
    enrollmentLink: "/app/style-guide-beta?campaign=style-guide-beta",
  } },
  // Makes the explicit Muse 2 Alpha comparison option available in prose pickers.
  { value: "muse2AlphaPhase2", label: "Muse 2 Alpha Phase 2 (Rate Prose)", oneClick: {
    adminApi: "/api/admin/muse2-alpha", accessName: "alpha",
    invitationField: "muse2AlphaInvitation", enrollmentField: "muse2AlphaEnrollment",
  } },
] as const satisfies readonly FeatureGateOption[];

export type FeatureGateValue = (typeof FEATURE_GATE_OPTIONS)[number]["value"];
